import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { useEffect, lazy, Suspense } from "react";
import axios from "axios";

// Configure axios defaults for secure authentication
// This ensures all API calls include credentials (cookies) for authentication
axios.defaults.withCredentials = true;

// Public Pages - Lazy loaded for performance
const HomePage = lazy(() => import("./pages/HomePage"));
const DestinationPage = lazy(() => import("./pages/DestinationPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const TestimonialsPage = lazy(() => import("./pages/TestimonialsPage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const FinlandPathwayPage = lazy(() => import("./pages/FinlandPathwayPage"));
const ProgramsPage = lazy(() => import("./pages/ProgramsPage"));
const ProgramDetailPage = lazy(() => import("./pages/ProgramDetailPage"));
const CareerLandingPage = lazy(() => import("./pages/CareerLandingPage"));

// Admin Pages - Lazy loaded
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminDestinations = lazy(() => import("./pages/admin/AdminDestinations"));
const AdminInquiries = lazy(() => import("./pages/admin/AdminInquiries"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminPromotionalTabs = lazy(() => import("./pages/admin/AdminPromotionalTabs"));
const AdminVideoTestimonials = lazy(() => import("./pages/admin/AdminVideoTestimonials"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminUserManagement = lazy(() => import("./pages/admin/AdminUserManagement"));
const AdminClients = lazy(() => import("./pages/admin/AdminClients"));
const AdminChecklistTemplates = lazy(() => import("./pages/admin/AdminChecklistTemplates"));
const AdminLeaderboard = lazy(() => import("./pages/admin/AdminLeaderboard"));
const AdminBulkUpload = lazy(() => import("./pages/admin/AdminBulkUpload"));
const AdminActivityLog = lazy(() => import("./pages/admin/AdminActivityLog"));
const AdminTasks = lazy(() => import("./pages/admin/AdminTasks"));
const AdminPromotions = lazy(() => import("./pages/admin/AdminPromotions"));
const KnowledgeBase = lazy(() => import("./pages/admin/KnowledgeBase"));
const StudentProfile = lazy(() => import("./pages/admin/StudentProfile"));
const AdminFacebookImport = lazy(() => import("./pages/admin/AdminFacebookImport"));
const AdminDailyReports = lazy(() => import("./pages/admin/AdminDailyReports"));
const Courses = lazy(() => import("./pages/admin/Courses"));
const ProductivityReports = lazy(() => import("./pages/admin/ProductivityReports"));
const OfficeVisits = lazy(() => import("./pages/admin/OfficeVisits"));
const FinancePage = lazy(() => import("./pages/admin/FinancePage"));
const StaffTips = lazy(() => import("./pages/admin/StaffTips"));
const Partners = lazy(() => import("./pages/admin/Partners"));

// Landing Pages
const FinlandNursingLanding = lazy(() => import("./pages/FinlandNursingLanding"));

// Client Portal Pages - Lazy loaded
const PortalLogin = lazy(() => import("./pages/portal/PortalLogin"));
const PortalLayout = lazy(() => import("./pages/portal/PortalLayout"));
const PortalDashboard = lazy(() => import("./pages/portal/PortalDashboard"));
const PortalChecklist = lazy(() => import("./pages/portal/PortalChecklist"));
const PortalTasks = lazy(() => import("./pages/portal/PortalTasks"));

// Layout Components - Not lazy loaded (needed immediately)
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import WhatsAppButton from "./components/layout/WhatsAppButton";
import AdminLayout from "./components/layout/AdminLayout";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import PortalProtectedRoute from "./components/auth/PortalProtectedRoute";
import AnalyticsTracker from "./components/analytics/AnalyticsTracker";

import "./App.css";

// Import unified spinner
import { PageSpinner } from "./components/ui/Spinner";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main className="min-h-screen">
        <Suspense fallback={<PageSpinner />}>
          {children}
        </Suspense>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AnalyticsTracker />
        <Suspense fallback={<PageSpinner />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            <Route path="/destinations/:slug" element={<PublicLayout><DestinationPage /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
            <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
            <Route path="/testimonials" element={<PublicLayout><TestimonialsPage /></PublicLayout>} />
            <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
            <Route path="/blog/:slug" element={<PublicLayout><BlogPostPage /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
            <Route path="/resources" element={<PublicLayout><ResourcesPage /></PublicLayout>} />
            <Route path="/resources/finland-pathway" element={<PublicLayout><FinlandPathwayPage /></PublicLayout>} />
            <Route path="/programs" element={<PublicLayout><ProgramsPage /></PublicLayout>} />
            <Route path="/programs/:id" element={<PublicLayout><ProgramDetailPage /></PublicLayout>} />
            <Route path="/careers/:slug" element={<PublicLayout><CareerLandingPage /></PublicLayout>} />
            
            {/* Landing Pages - No Navbar/Footer for cleaner conversion */}
            <Route path="/finland-nursing" element={<FinlandNursingLanding />} />
            
            {/* Admin Routes - /tothemoon */}
            <Route path="/tothemoon/login" element={<AdminLogin />} />
            <Route path="/tothemoon" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUserManagement />} />
              <Route path="destinations" element={<AdminDestinations />} />
              <Route path="inquiries" element={<AdminInquiries />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="checklists" element={<AdminChecklistTemplates />} />
              <Route path="leaderboard" element={<AdminLeaderboard />} />
              <Route path="bulk-upload" element={<AdminBulkUpload />} />
              <Route path="facebook-import" element={<AdminFacebookImport />} />
              <Route path="activity" element={<AdminActivityLog />} />
              <Route path="tasks" element={<AdminTasks />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="promotional-tabs" element={<AdminPromotionalTabs />} />
              <Route path="video-testimonials" element={<AdminVideoTestimonials />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="promotions" element={<AdminPromotions />} />
              <Route path="knowledge-base" element={<KnowledgeBase />} />
              <Route path="students/:clientId" element={<StudentProfile />} />
              <Route path="daily-reports" element={<AdminDailyReports />} />
              <Route path="courses" element={<Courses />} />
              <Route path="productivity" element={<ProductivityReports />} />
              <Route path="office-visits" element={<OfficeVisits />} />
              <Route path="finance" element={<FinancePage />} />
              <Route path="staff-tips" element={<StaffTips />} />
              <Route path="partners" element={<Partners />} />
            </Route>
            
            {/* Client Portal Routes - /portal */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal" element={<PortalProtectedRoute><PortalLayout /></PortalProtectedRoute>}>
              <Route index element={<PortalDashboard />} />
              <Route path="checklist" element={<PortalChecklist />} />
              <Route path="tasks" element={<PortalTasks />} />
            </Route>
            
            {/* Redirect old /admin to /tothemoon */}
            <Route path="/admin/*" element={<Navigate to="/tothemoon" replace />} />
            <Route path="/admin" element={<Navigate to="/tothemoon" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
